import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarPlus, ArrowLeft, Clock } from "lucide-react";

export default function Extend() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [extHours, setExtHours] = useState(2);
  const [hoursBusy, setHoursBusy] = useState(false);

  const submitHours = async () => {
    setHoursBusy(true);
    try {
      const stored = localStorage.getItem(`booking_email_${booking.id}`) || email;
      const { data } = await api.post(`/bookings/${booking.id}/extend-hours-guest`, {
        hours: extHours,
        origin_url: window.location.origin,
        email: user ? undefined : stored,
      });
      window.location.href = data.checkout_url;
    } catch (e2) {
      toast.error(errMsg(e2));
      setHoursBusy(false);
    }
  };

  useEffect(() => {
    if (bookingId) {
      api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch((e) => setError(errMsg(e)));
    }
  }, [bookingId]);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.get("/bookings/lookup", { params: { ref, email } });
      setBooking(data);
      localStorage.setItem(`booking_email_${data.id}`, email.trim().toLowerCase());
    } catch (e2) {
      setError(errMsg(e2));
    }
  };

  const extraDays = useMemo(() => {
    if (!booking || !newEnd) return 0;
    return Math.round((new Date(newEnd) - new Date(booking.end_date)) / 86400000);
  }, [booking, newEnd]);

  const base = booking && extraDays > 0 ? extraDays * booking.daily_rate : 0;
  const fee = base * 0.03;
  const amount = base + fee;

  const minDate = booking
    ? new Date(new Date(booking.end_date).getTime() + 86400000).toISOString().slice(0, 10)
    : "";

  const submit = async () => {
    if (extraDays < 1) return toast.error("Choose a return date after the current one");
    setLoading(true);
    try {
      const stored = localStorage.getItem(`booking_email_${booking.id}`) || email;
      const { data } = await api.post(`/bookings/${booking.id}/extend`, {
        new_end_date: newEnd,
        origin_url: window.location.origin,
        email: user ? undefined : stored,
      });
      window.location.href = data.checkout_url;
    } catch (e2) {
      toast.error(errMsg(e2));
      setLoading(false);
    }
  };

  if (!booking) {
    return (
      <div data-testid="extend-lookup" className="max-w-md mx-auto px-6 pt-40 pb-24">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Rental Extension</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight">
          Extend your <span className="italic gold-text">rental</span>
        </h1>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          Enter your booking reference and email to instantly extend your rental online.
        </p>
        {bookingId && error && <p className="mt-4 text-sm text-destructive font-ui">{error} — find your booking manually below.</p>}
        <form onSubmit={lookup} className="mt-8 space-y-5">
          <div>
            <Label className="font-ui text-xs tracking-wide text-muted-foreground">Booking reference (e.g. ED-1A2B3C)</Label>
            <Input data-testid="extend-ref-input" required value={ref} onChange={(e) => setRef(e.target.value)} className="input-dark mt-2" />
          </div>
          <div>
            <Label className="font-ui text-xs tracking-wide text-muted-foreground">Email used at booking</Label>
            <Input data-testid="extend-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark mt-2" />
          </div>
          {error && !bookingId && <p data-testid="extend-lookup-error" className="text-sm text-destructive font-ui">{error}</p>}
          <Button data-testid="extend-lookup-button" className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-11">
            Find My Booking
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="extend-page" className="max-w-xl mx-auto px-6 pt-36 pb-24">
      <Link to={user ? "/account" : "/"} className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2">
        <ArrowLeft size={15} /> Back
      </Link>
      <div className="mt-8 bg-card border border-border rounded-2xl p-8">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
          <CalendarPlus size={14} className="gold-text" /> Rental Extension
        </p>
        <h1 className="mt-4 font-display text-3xl tracking-tight">{booking.vehicle_name}</h1>
        <p className="mt-2 text-sm text-muted-foreground font-ui">
          {booking.booking_ref} · current return: <span className="text-foreground">{fmtDate(booking.end_date)}</span>
        </p>
        <div className="mt-6">
          <Label className="font-ui text-xs tracking-wide text-muted-foreground">New return date</Label>
          <Input data-testid="extend-new-date" type="date" min={minDate} value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="input-dark mt-2" />
        </div>
        {extraDays > 0 && (
          <div data-testid="extend-price-summary" className="mt-6 border border-border rounded-xl p-5 space-y-2 text-sm font-ui">
            <div className="flex justify-between"><span className="text-muted-foreground">{extraDays} extra {extraDays === 1 ? "day" : "days"} × {fmtMoney(booking.daily_rate)}</span><span>{fmtMoney(base)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Processing fee (3%)</span><span>{fmtMoney(fee)}</span></div>
            <div className="flex justify-between pt-2 border-t border-border"><span className="font-semibold">Due now</span><span className="gold-text text-xl font-semibold">{fmtMoney(amount)}</span></div>
          </div>
        )}
        <Button data-testid="extend-pay-button" disabled={extraDays < 1 || loading} onClick={submit} className="mt-6 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-11">
          {loading ? "Preparing checkout…" : "Pay & Extend Instantly"}
        </Button>
        <p className="mt-3 text-sm text-muted-foreground font-ui">
          Instant confirmation by secure card checkout. Extend before or on your return date — no late fees, ever. Your booking number stays the same, your invoice updates automatically, and once payment is confirmed we'll email + text you a link to re-sign your renewed contract.
        </p>
        <div className="mt-6 pt-6 border-t border-border">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2"><Clock size={13} className="gold-text" /> Just a few extra hours?</p>
          <p className="mt-2 text-xs text-muted-foreground font-ui leading-relaxed">Running a little late on return day? Extend by the hour at $25/hr — no full-day charge, no late fees within the extra time.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <select data-testid="extend-hours-select" value={extHours} onChange={(e) => setExtHours(Number(e.target.value))} className="input-dark h-10 rounded-full px-4 text-sm font-ui">
              {Array.from({ length: 11 }, (_, i) => i + 1).map((h) => (
                <option key={h} value={h}>{h} hour{h > 1 ? "s" : ""} — ${h * 25}</option>
              ))}
            </select>
            <Button data-testid="extend-hours-button" variant="outline" disabled={hoursBusy} onClick={submitHours} className="rounded-full font-ui border-amber-300 text-amber-700 hover:bg-amber-50">
              {hoursBusy ? "Preparing checkout…" : `Pay $${extHours * 25} & extend`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
